export type StoredLead = {
  fullName: string;
  email: string;
  phone?: string;
  betrugsart?: string;
  damageAmount?: string;
  message?: string;
  createdAt: string;
};

export type StoredBooking = {
  id: string;
  leadEmail?: string;
  agentId: string;
  agentName: string;
  dateISO: string;
  time: string;
  createdAt: string;
  sent?: boolean; // whether POST to server succeeded
};

const LEADS_KEY = "vm_leads";
const BOOKINGS_KEY = "vm_bookings";

function read<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw) as T[];
  } catch {
    return [];
  }
}

function write<T>(key: string, arr: T[]) {
  try {
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (e) {
    console.error("localStorage write failed", e);
  }
}

export function saveLead(lead: Omit<StoredLead, "createdAt">) {
  const entry: StoredLead = { ...lead, createdAt: new Date().toISOString() };
  const all = read<StoredLead>(LEADS_KEY);
  all.push(entry);
  write(LEADS_KEY, all);
  return entry;
}

export function saveBooking(booking: Omit<StoredBooking, "id" | "createdAt" | "sent">) {
  const entry: StoredBooking = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    ...booking,
    createdAt: new Date().toISOString(),
  };
  const all = read<StoredBooking>(BOOKINGS_KEY);
  all.push(entry);
  write(BOOKINGS_KEY, all);
  return entry;
}

export async function sendBookingToServer(payload: {
  name: string;
  email: string;
  phone?: string;
  betrugsart?: string;
  damageAmount?: string;
  message?: string;
  agentId: string;
  agentName: string;
  dateISO: string;
  time: string;
}) {
  // try to send to /send-email if available; if not, fail gracefully
  try {
    const res = await fetch("https://api.veritasberatung.com/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        betrugsart: payload.betrugsart,
        damageAmount: payload.damageAmount,
        message: `📅 Terminbuchung\nErmittler: ${payload.agentName} (ID: ${payload.agentId})\nDatum: ${new Date(payload.dateISO).toLocaleDateString("de-DE")}\nUhrzeit: ${payload.time}\n\n${payload.message || "-"}`,
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, error: text || `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (err: any) {
    return { ok: false, error: err?.message || String(err) };
  }
}

export async function storeAndSendBooking(args: {
  lead: Omit<StoredLead, "createdAt">;
  booking: Omit<StoredBooking, "id" | "createdAt" | "sent">;
}) {
  // persist lead
  const savedLead = saveLead(args.lead);
  // save booking referencing lead email if present
  const savedBooking = saveBooking({ ...args.booking, leadEmail: args.lead.email });

  // try to send to server; if succeeds, mark booking as sent
  const result = await sendBookingToServer({
    name: args.lead.fullName,
    email: args.lead.email,
    phone: args.lead.phone,
    betrugsart: args.lead.betrugsart,
    damageAmount: args.lead.damageAmount,
    message: args.lead.message,
    agentId: args.booking.agentId,
    agentName: args.booking.agentName,
    dateISO: args.booking.dateISO,
    time: args.booking.time,
  });

  if (result.ok) {
    // update stored booking to mark sent
    const all = read<StoredBooking>(BOOKINGS_KEY);
    const idx = all.findIndex((b) => b.id === savedBooking.id);
    if (idx >= 0) {
      all[idx] = { ...all[idx], sent: true };
      write(BOOKINGS_KEY, all);
    }
  }

  return { savedLead, savedBooking, sent: result.ok, error: result.error };
}
